import React, { useState } from "react";
import { Check } from "lucide-react";
import Navbar from "../Navbar/Navbar";
import axios from "axios";
import ENV from "../config";

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const FEATURES = [
  "Unlimited trade analysis",
  "A score for every buy and sell, with the reasons",
  "Sector and Nifty context at entry",
  "Drawdown and peak after every trade",
  "Pulse dashboard: your mistakes and habits, in rupees",
  "Support and resistance levels, and candle patterns",
  "Charts with RSI, EMAs, volume and drawing tools",
  "Notes on every trade",
  "Shareable analysis images",
];

const PLANS = [
  {
    id: "monthly",
    name: "Monthly",
    amount: 99,
    was: "₹199",
    now: "₹99",
    unit: "/month",
    line: "Start for less than a cup of coffee. No big upfront payment.",
    cta: "Start for ₹99",
  },
  {
    id: "yearly",
    name: "Yearly",
    amount: 999,
    was: "₹1,999",
    now: "₹999",
    unit: "/year",
    line: "Just ₹83 a month, less than a movie ticket. Save ₹189.",
    cta: "Get Yearly Access",
    featured: true,
  },
];

const auth = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem("jwtToken")}`,
    "Content-Type": "application/json",
  },
});

export default function SubscriptionPlans() {
  const [loadingPlan, setLoadingPlan] = useState(null);

  const handlePayment = async (plan) => {
    if (loadingPlan) return;
    setLoadingPlan(plan.id);

    const loaded = await loadRazorpay();
    if (!loaded) {
      setLoadingPlan(null);
      return alert("Couldn't load the payment window. Please check your connection and try again.");
    }

    let res;
    try {
      // The backend should price the order from `plan`, not trust `amount` from the browser.
      res = await axios.post(
        `${ENV.BASE_API_URL}/api/razorpay_order/`,
        { amount: plan.amount, plan: plan.id },
        auth()
      );
    } catch (err) {
      setLoadingPlan(null);
      return alert(err.response?.data?.detail || "Something went wrong");
    }

    const data = res.data;
    const rzp = new window.Razorpay({
      key: data.key,
      amount: data.amount,
      currency: data.currency,
      order_id: data.orderId,
      name: "Tradeye",
      description: `${plan.name} plan`,
      modal: { ondismiss: () => setLoadingPlan(null) },
      handler: async (response) => {
        try {
          await axios.post(`${ENV.BASE_API_URL}/api/razorpay_callback/`, response, auth());
          window.location.href = "/";
        } catch (err) {
          setLoadingPlan(null);
          alert("Your payment went through but we couldn't activate your plan yet. Please contact support with your payment ID: " + response.razorpay_payment_id);
        }
      },
    });
    rzp.open();
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-slate-50 px-6 py-4 md:py-20 antialiased">
        <div className="mx-auto max-w-4xl">
          <div className="mx-auto max-w-xl text-center">
            <h1 className="text-3xl md:text-[42px] md:leading-[1.15] font-extrabold tracking-tight text-slate-900">
              Stop repeating the mistakes that cost you money.
            </h1>
          </div>

          <div className="mt-5 grid gap-6 md:grid-cols-2 md:items-center">
            {PLANS.map((plan) => (
              <div
                key={plan.id}
                className={`relative flex flex-col rounded-[14px] bg-white ${
                  plan.featured
                    ? "overflow-hidden border-2 border-slate-900 px-9 pb-9 pt-11 shadow-[0_16px_48px_rgba(17,24,39,0.12)]"
                    : "border border-slate-200 p-8 shadow-[0_12px_40px_rgba(17,24,39,0.06)]"
                }`}
              >
                {plan.featured && (
                  <div
                    className="absolute right-0 top-0 bg-slate-900 py-2 pl-6 pr-4 text-[11px] font-bold tracking-widest text-white"
                    style={{ clipPath: "polygon(0 0, 100% 0, 100% 100%, 12px 100%, 0 calc(100% - 12px))" }}
                  >
                    BEST VALUE
                  </div>
                )}

                <div className="text-lg font-bold text-slate-900">{plan.name}</div>

                <div className="mt-3 flex items-baseline gap-2.5">
                  <span className="text-[22px] font-medium text-slate-300 line-through">{plan.was}</span>
                  <span className="text-6xl font-black leading-none tracking-tighter text-slate-900">{plan.now}</span>
                  <span className="text-lg text-slate-500">{plan.unit}</span>
                </div>

                <div className="mt-3 text-xs font-bold uppercase tracking-[0.12em] text-emerald-700">
                  Limited-time launch offer
                </div>
                <p className="mt-3 text-[15px] font-semibold text-slate-700">{plan.line}</p>

                <ul className="my-5 space-y-2.5">
                  {FEATURES.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm leading-snug text-slate-600">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" strokeWidth={2} />
                      {f}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handlePayment(plan)}
                  disabled={!!loadingPlan}
                  className={`mt-auto w-full rounded-2xl border-2 border-slate-900 py-4 text-lg font-bold transition active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 ${
                    plan.featured
                      ? "bg-slate-900 text-white hover:bg-slate-800"
                      : "bg-white text-slate-900 hover:bg-slate-50"
                  }`}
                >
                  {loadingPlan === plan.id ? "Opening checkout..." : plan.cta}
                </button>
              </div>
            ))}
          </div>

          <p className="mt-10 text-center text-sm text-slate-500">Secure checkout. Instant access.</p>
        </div>
      </div>
    </>
  );
}